import { blobToHex, divideArrayIntoChunks, hexToBlob } from '@pkg/basic'
import { type LowerHex, createEvmActionSource } from '@pkg/evm'
import { createPolkadotSource } from '@pkg/polkadot'
import { createSolanaActionSource } from '@pkg/solana'
import { type SQL, and, eq, inArray, ne, not, notInArray } from 'drizzle-orm'
import { sqliteMaxHostParameterCount } from '../constants'
import { deleteAssistantProposalsForActionIds } from '../query/write/deleteAssistantProposalsForActionKeys'
import {
  accountDisplayOrderTable,
  accountEvmTxIndexRelationTable,
  accountPolkadotExtrinsicRelationTable,
  accountSolanaSignatureTable,
  accountTable,
  actionAccountRelationTable,
  actionSourceTable,
  actionTable,
  cardanoEventIndexTable,
  portfolioTable,
  xrplTransactionIndexTable,
} from '../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../types'

/**
 * Selector for targeting accounts for deletion.
 * Can target either by group name or by specific account IDs.
 */
type AccountSelector =
  | {
      /** Target accounts by group name */
      type: 'group'
      /** The group name to target */
      group: string
    }
  | {
      /** Target specific account IDs */
      type: 'ids'
      /** Array of account IDs to target */
      accountIds: number[]
    }

/**
 * Context object containing all data needed for account deletion operations.
 * Includes related actions, sources, and blockchain transaction references.
 */
type DeletionContext = {
  /** All action IDs related to the accounts being deleted */
  actionIds: string[]
  /** Action IDs that can be safely deleted (not referenced by other accounts) */
  deleteActionIds: string[]
  /** Source IDs that only contain actions being deleted */
  deletingSources: string[]
  /** EVM transaction hashes related to the accounts */
  evmTxHashes: Uint8Array[]
  /** Polkadot block numbers related to the accounts */
  polkadotBlockNumbers: number[]
  /** Solana signatures related to the accounts */
  solanaSignatures: string[]
  /** 削除対象が関与した Cardano Tx の再解析対象 Source ID。 */
  xrplSourceIds: string[]
  cardanoSourceIds: string[]
}

type EvmSourceReference = {
  hash: Uint8Array
  chainId: number | null
}

type PolkadotSourceReference = {
  blockNumber: number
  extrinsicIndex: number | null
  chainId: number | null
}

type SolanaSourceReference = {
  signature: string
}

type ActionIdRow = {
  id: string
}

type AccountIdColumn =
  | typeof accountEvmTxIndexRelationTable.accountId
  | typeof accountPolkadotExtrinsicRelationTable.accountId
  | typeof accountSolanaSignatureTable.accountId
  | typeof actionAccountRelationTable.accountId

/**
 * account selector から accountTable 用の where 条件を生成する。
 *
 * @param params - selector 情報
 * @returns accountTable を対象にした where 条件
 */
const createAccountFilter = ({
  selector,
}: {
  selector: AccountSelector
}): SQL<unknown> => {
  if (selector.type === 'group') {
    return eq(accountTable.group, selector.group)
  }

  return inArray(accountTable.id, selector.accountIds)
}

/**
 * 削除対象以外の account を表す where 条件を生成する。
 *
 * @param params - selector 情報と relation 側 accountId column
 * @returns 削除対象以外の account を表す where 条件
 */
const createOtherAccountsFilter = ({
  selector,
  accountIdColumn,
}: {
  selector: AccountSelector
  accountIdColumn: AccountIdColumn
}): SQL<unknown> => {
  if (selector.type === 'group') {
    return ne(accountTable.group, selector.group)
  }

  return not(inArray(accountIdColumn, selector.accountIds))
}

/**
 * relation 行と source 復元ロジックから、他 account と共有していない source id に紐づく action id 一覧を取得する。
 *
 * @param params - DB、source id 一覧、共有 source id 一覧
 * @returns 排他的な source id に紐づく action id 一覧
 */
const getActionIdsFromExclusiveSourceIds = async ({
  database,
  sourceIds,
}: {
  database: DatabaseWithTransaction['database']
  sourceIds: readonly string[]
}): Promise<string[]> => {
  return getActionIdsBySourceIds({
    database,
    sourceIds,
  })
}

/**
 * 直接 relation を持つ action と、source から復元した action を結合して重複なく返す。
 *
 * @param params - 各 action id 行
 * @returns 重複を除いた action id 一覧
 */
const mergeActionIds = ({
  actionRows,
  evmActionIds,
  polkadotActionIds,
  solanaActionIds,
}: {
  actionRows: readonly ActionIdRow[]
  evmActionIds: readonly string[]
  polkadotActionIds: readonly string[]
  solanaActionIds: readonly string[]
}): string[] => {
  return [
    ...new Set([
      ...actionRows.map((row) => row.id),
      ...evmActionIds,
      ...polkadotActionIds,
      ...solanaActionIds,
    ]),
  ]
}

/**
 * EVM tx relation 行から action source id 一覧を生成する。
 *
 * @param params - relation 行の一覧
 * @returns 重複を除いた EVM action source id 一覧
 */
const createEvmSourceIds = ({
  rows,
}: {
  rows: readonly EvmSourceReference[]
}): string[] => {
  const sourceIds = new Set<string>()

  for (const row of rows) {
    if (row.chainId === null) {
      continue
    }
    sourceIds.add(
      createEvmActionSource({
        chainId: row.chainId,
        transactionHash: blobToHex(row.hash),
      }),
    )
  }

  return [...sourceIds]
}

/**
 * Polkadot extrinsic relation 行から action source id 一覧を生成する。
 *
 * @param params - relation 行の一覧
 * @returns 重複を除いた Polkadot action source id 一覧
 */
const createPolkadotSourceIds = ({
  rows,
}: {
  rows: readonly PolkadotSourceReference[]
}): string[] => {
  const sourceIds = new Set<string>()

  for (const row of rows) {
    if (row.chainId === null) {
      continue
    }
    sourceIds.add(
      createPolkadotSource({
        chainId: row.chainId,
        blockNumber: row.blockNumber,
        extrinsicIndex: row.extrinsicIndex ?? undefined,
      }),
    )
  }

  return [...sourceIds]
}

/**
 * Solana signature relation 行から action source id 一覧を生成する。
 *
 * @param params - relation 行の一覧
 * @returns 重複を除いた Solana action source id 一覧
 */
const createSolanaSourceIds = ({
  rows,
}: {
  rows: readonly SolanaSourceReference[]
}): ReturnType<typeof createSolanaActionSource>[] => {
  return [
    ...new Set(
      rows.map(({ signature }) => createSolanaActionSource({ signature })),
    ),
  ]
}

/**
 * 削除対象以外の account にも紐づく EVM source id を除外する。
 *
 * @param params - DB と selector と relation 行の一覧
 * @returns 削除対象 account にのみ紐づく EVM source id 一覧
 */
const getExclusiveEvmSourceIds = async ({
  database,
  selector,
  rows,
}: {
  database: DatabaseWithTransaction['database']
  selector: AccountSelector
  rows: readonly EvmSourceReference[]
}): Promise<string[]> => {
  const rowMap = new Map<string, EvmSourceReference>()

  for (const row of rows) {
    if (row.chainId === null) {
      continue
    }
    rowMap.set(`${row.chainId}:${blobToHex(row.hash)}`, row)
  }

  if (rowMap.size === 0) {
    return []
  }

  const sharedSourceIds = new Set<string>()
  const rowsByChain = new Map<number, LowerHex[]>()
  for (const row of rowMap.values()) {
    const chainRows = rowsByChain.get(row.chainId ?? -1) ?? []
    chainRows.push(blobToHex(row.hash))
    rowsByChain.set(row.chainId ?? -1, chainRows)
  }

  for (const [chainId, hashes] of rowsByChain.entries()) {
    const sharedRows = await Promise.all(
      divideArrayIntoChunks(hashes, sqliteMaxHostParameterCount - 1).map(
        (chunk) =>
          database
            .selectDistinct({ hash: accountEvmTxIndexRelationTable.hash })
            .from(accountEvmTxIndexRelationTable)
            .innerJoin(
              accountTable,
              eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
            )
            .where(
              and(
                eq(accountTable.evmChainId, chainId),
                inArray(
                  accountEvmTxIndexRelationTable.hash,
                  chunk.map((hash) => hexToBlob(hash)),
                ),
                createOtherAccountsFilter({
                  selector,
                  accountIdColumn: accountEvmTxIndexRelationTable.accountId,
                }),
              ),
            ),
      ),
    )

    for (const row of sharedRows.flat()) {
      sharedSourceIds.add(
        createEvmActionSource({
          chainId,
          transactionHash: blobToHex(row.hash),
        }),
      )
    }
  }

  return createEvmSourceIds({ rows }).filter(
    (sourceId) => !sharedSourceIds.has(sourceId),
  )
}

/**
 * 削除対象以外の account にも紐づく Polkadot source id を除外する。
 *
 * @param params - DB と selector と relation 行の一覧
 * @returns 削除対象 account にのみ紐づく Polkadot source id 一覧
 */
const getExclusivePolkadotSourceIds = async ({
  database,
  selector,
  rows,
}: {
  database: DatabaseWithTransaction['database']
  selector: AccountSelector
  rows: readonly PolkadotSourceReference[]
}): Promise<string[]> => {
  const rowMap = new Map<string, PolkadotSourceReference>()

  for (const row of rows) {
    if (row.chainId === null) {
      continue
    }
    rowMap.set(
      `${row.chainId}:${row.blockNumber}:${row.extrinsicIndex ?? 'null'}`,
      row,
    )
  }

  if (rowMap.size === 0) {
    return []
  }

  const sharedSourceIds = new Set<string>()
  const rowsByChain = new Map<number, number[]>()
  for (const row of rowMap.values()) {
    const chainRows = rowsByChain.get(row.chainId ?? -1) ?? []
    chainRows.push(row.blockNumber)
    rowsByChain.set(row.chainId ?? -1, chainRows)
  }

  for (const [chainId, blockNumbers] of rowsByChain.entries()) {
    const sharedRows = await Promise.all(
      divideArrayIntoChunks(
        [...new Set(blockNumbers)],
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            blockNumber: accountPolkadotExtrinsicRelationTable.blockNumber,
            extrinsicIndex:
              accountPolkadotExtrinsicRelationTable.extrinsicIndex,
          })
          .from(accountPolkadotExtrinsicRelationTable)
          .innerJoin(
            accountTable,
            eq(
              accountPolkadotExtrinsicRelationTable.accountId,
              accountTable.id,
            ),
          )
          .where(
            and(
              eq(accountTable.polkadotChainId, chainId),
              inArray(accountPolkadotExtrinsicRelationTable.blockNumber, [
                ...chunk,
              ]),
              createOtherAccountsFilter({
                selector,
                accountIdColumn:
                  accountPolkadotExtrinsicRelationTable.accountId,
              }),
            ),
          ),
      ),
    )

    for (const row of sharedRows.flat()) {
      const key = `${chainId}:${row.blockNumber}:${row.extrinsicIndex ?? 'null'}`
      const sourceRow = rowMap.get(key)
      if (sourceRow === undefined) {
        continue
      }
      sharedSourceIds.add(
        createPolkadotSource({
          chainId,
          blockNumber: sourceRow.blockNumber,
          extrinsicIndex: sourceRow.extrinsicIndex ?? undefined,
        }),
      )
    }
  }

  return createPolkadotSourceIds({ rows }).filter(
    (sourceId) => !sharedSourceIds.has(sourceId),
  )
}

/**
 * 削除対象以外の account にも紐づく Solana source id を除外する。
 *
 * @param params - DB と selector と relation 行の一覧
 * @returns 削除対象 account にのみ紐づく Solana source id 一覧
 */
const getExclusiveSolanaSourceIds = async ({
  database,
  selector,
  rows,
}: {
  database: DatabaseWithTransaction['database']
  selector: AccountSelector
  rows: readonly SolanaSourceReference[]
}): Promise<string[]> => {
  const signatures = [...new Set(rows.map(({ signature }) => signature))]

  if (signatures.length === 0) {
    return []
  }

  const sharedRows = await Promise.all(
    divideArrayIntoChunks(signatures, sqliteMaxHostParameterCount - 1).map(
      (chunk) =>
        database
          .selectDistinct({ signature: accountSolanaSignatureTable.signature })
          .from(accountSolanaSignatureTable)
          .innerJoin(
            accountTable,
            eq(accountSolanaSignatureTable.accountId, accountTable.id),
          )
          .where(
            and(
              inArray(accountSolanaSignatureTable.signature, [...chunk]),
              createOtherAccountsFilter({
                selector,
                accountIdColumn: accountSolanaSignatureTable.accountId,
              }),
            ),
          ),
    ),
  )

  const sharedSignatureSet = new Set(
    sharedRows
      .flat()
      .map(({ signature }) => createSolanaActionSource({ signature })),
  )

  return createSolanaSourceIds({ rows }).filter(
    (sourceId) => !sharedSignatureSet.has(sourceId),
  )
}

/**
 * source id 一覧に紐づく action id 一覧を取得する。
 *
 * @param params - DB と source id 一覧
 * @returns 重複を除いた action id 一覧
 */
const getActionIdsBySourceIds = async ({
  database,
  sourceIds,
}: {
  database: DatabaseWithTransaction['database']
  sourceIds: readonly string[]
}): Promise<string[]> => {
  if (sourceIds.length === 0) {
    return []
  }

  const actionRows = await Promise.all(
    divideArrayIntoChunks(sourceIds, sqliteMaxHostParameterCount - 1).map(
      (chunk) =>
        database
          .select({ id: actionTable.id })
          .from(actionTable)
          .where(inArray(actionTable.sourceId, [...chunk])),
    ),
  )

  return [...new Set(actionRows.flat().map((row) => row.id))]
}

/**
 * 削除対象 action のみで構成される action source を抽出する。
 *
 * @param params - DB と削除対象 action id 一覧
 * @returns 削除可能な source id 一覧
 */
const getDeletingSourceIds = async ({
  database,
  deleteActionIds,
}: {
  database: DatabaseWithTransaction['database']
  deleteActionIds: readonly string[]
}): Promise<string[]> => {
  if (deleteActionIds.length === 0) {
    return []
  }

  const deleteActionIdSet = new Set(deleteActionIds)
  const sourceRows = await Promise.all(
    divideArrayIntoChunks(deleteActionIds, sqliteMaxHostParameterCount - 1).map(
      (chunk) =>
        database
          .select({ source: actionTable.sourceId })
          .from(actionTable)
          .where(inArray(actionTable.id, [...chunk])),
    ),
  )
  const candidateSourceIds = [
    ...new Set(sourceRows.flat().map((row) => row.source)),
  ]

  if (candidateSourceIds.length === 0) {
    return []
  }

  const sourceActionRows = await Promise.all(
    divideArrayIntoChunks(
      candidateSourceIds,
      sqliteMaxHostParameterCount - 1,
    ).map((chunk) =>
      database
        .select({ source: actionTable.sourceId, actionId: actionTable.id })
        .from(actionTable)
        .where(inArray(actionTable.sourceId, [...chunk])),
    ),
  )

  const sourceActionsMap = new Map<string, string[]>()
  for (const row of sourceActionRows.flat()) {
    const actionIds = sourceActionsMap.get(row.source) ?? []
    sourceActionsMap.set(row.source, [...actionIds, row.actionId])
  }

  const deletingSources: string[] = []
  for (const [sourceId, actionIds] of sourceActionsMap.entries()) {
    if (actionIds.every((actionId) => deleteActionIdSet.has(actionId))) {
      deletingSources.push(sourceId)
    }
  }

  return deletingSources
}

/**
 * 削除対象が関与した Cardano Tx を Action が存在しない内部移動も含めて取得する。
 * @param params DB と削除対象の指定
 * @returns 残存アカウントで再解析が必要な Source ID 一覧
 */
const getXrplDeletionSourceIds = async (params: {
  database: DatabaseWithTransaction['database']
  selector: AccountSelector
}): Promise<string[]> => {
  const { database, selector } = params
  const filters =
    selector.type === 'group'
      ? [eq(accountTable.group, selector.group)]
      : divideArrayIntoChunks(
          selector.accountIds,
          sqliteMaxHostParameterCount - 1,
        ).map((chunk) => inArray(accountTable.id, [...chunk]))
  const rows = await Promise.all(
    filters.map((filter) =>
      database
        .selectDistinct({ sourceId: xrplTransactionIndexTable.sourceId })
        .from(xrplTransactionIndexTable)
        .innerJoin(
          accountTable,
          eq(xrplTransactionIndexTable.accountId, accountTable.id),
        )
        .where(filter),
    ),
  )
  return [...new Set(rows.flat().map((row) => row.sourceId))]
}

const getCardanoDeletionSourceIds = async (params: {
  database: DatabaseWithTransaction['database']
  selector: AccountSelector
}): Promise<string[]> => {
  const { database, selector } = params
  const filters =
    selector.type === 'group'
      ? [eq(accountTable.group, selector.group)]
      : divideArrayIntoChunks(
          selector.accountIds,
          sqliteMaxHostParameterCount - 1,
        ).map((chunk) => inArray(accountTable.id, [...chunk]))
  const rows = await Promise.all(
    filters.map((filter) =>
      database
        .selectDistinct({ sourceId: cardanoEventIndexTable.sourceId })
        .from(cardanoEventIndexTable)
        .innerJoin(
          accountTable,
          eq(cardanoEventIndexTable.accountId, accountTable.id),
        )
        .where(and(filter, eq(cardanoEventIndexTable.kind, 'tx'))),
    ),
  )
  return [...new Set(rows.flat().map((row) => row.sourceId))]
}

/**
 * Retrieves all data related to the accounts selected for deletion.
 * This includes actions, blockchain transaction references, and determines
 * which actions and sources can be safely deleted.
 *
 * @param database - Database instance for querying
 * @param selector - Account selector (by group or IDs)
 * @returns Promise resolving to deletion context with all related data
 */
const getRelatedData = async (
  database: DatabaseWithTransaction['database'],
  selector: AccountSelector,
): Promise<DeletionContext> => {
  const accountFilter = createAccountFilter({ selector })
  const xrplSourceIds = await getXrplDeletionSourceIds({ database, selector })
  const cardanoSourceIds = await getCardanoDeletionSourceIds({
    database,
    selector,
  })

  // For group selector, use simple queries
  if (selector.type === 'group') {
    const [actionRows, evmTxRows, polkadotRows, solanaRows] = await Promise.all(
      [
        database
          .select({
            id: actionTable.id,
            timestamp: actionTable.timestamp,
          })
          .from(actionTable)
          .innerJoin(
            actionAccountRelationTable,
            eq(actionTable.id, actionAccountRelationTable.actionId),
          )
          .innerJoin(
            accountTable,
            eq(actionAccountRelationTable.accountId, accountTable.id),
          )
          .where(accountFilter),
        database
          .selectDistinct({
            hash: accountEvmTxIndexRelationTable.hash,
            chainId: accountTable.evmChainId,
          })
          .from(accountEvmTxIndexRelationTable)
          .innerJoin(
            accountTable,
            eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
          )
          .where(accountFilter),
        database
          .selectDistinct({
            blockNumber: accountPolkadotExtrinsicRelationTable.blockNumber,
            extrinsicIndex:
              accountPolkadotExtrinsicRelationTable.extrinsicIndex,
            chainId: accountTable.polkadotChainId,
          })
          .from(accountPolkadotExtrinsicRelationTable)
          .innerJoin(
            accountTable,
            eq(
              accountPolkadotExtrinsicRelationTable.accountId,
              accountTable.id,
            ),
          )
          .where(accountFilter),
        database
          .selectDistinct({ signature: accountSolanaSignatureTable.signature })
          .from(accountSolanaSignatureTable)
          .innerJoin(
            accountTable,
            eq(accountSolanaSignatureTable.accountId, accountTable.id),
          )
          .where(accountFilter),
      ],
    )

    const evmActionIds = await getActionIdsFromExclusiveSourceIds({
      database,
      sourceIds: await getExclusiveEvmSourceIds({
        database,
        selector,
        rows: evmTxRows,
      }),
    })
    const polkadotActionIds = await getActionIdsFromExclusiveSourceIds({
      database,
      sourceIds: await getExclusivePolkadotSourceIds({
        database,
        selector,
        rows: polkadotRows,
      }),
    })
    const solanaActionIds = await getActionIdsFromExclusiveSourceIds({
      database,
      sourceIds: await getExclusiveSolanaSourceIds({
        database,
        selector,
        rows: solanaRows,
      }),
    })
    const actionIds = mergeActionIds({
      actionRows,
      evmActionIds,
      polkadotActionIds,
      solanaActionIds,
    })
    const { deleteActionIds, deletingSources } = await processActions(
      database,
      actionIds,
      selector,
    )

    return {
      actionIds,
      deleteActionIds,
      deletingSources,
      evmTxHashes: evmTxRows.map((row) => row.hash),
      polkadotBlockNumbers: polkadotRows.map((row) => row.blockNumber),
      xrplSourceIds,
      cardanoSourceIds,
      solanaSignatures: solanaRows.map((row) => row.signature),
    }
  }

  // For IDs selector, use chunked queries
  const [actionRows, evmTxRows, polkadotRows, solanaRows] = await Promise.all([
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .select({
            id: actionTable.id,
            timestamp: actionTable.timestamp,
          })
          .from(actionTable)
          .innerJoin(
            actionAccountRelationTable,
            eq(actionTable.id, actionAccountRelationTable.actionId),
          )
          .innerJoin(
            accountTable,
            eq(actionAccountRelationTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            hash: accountEvmTxIndexRelationTable.hash,
            chainId: accountTable.evmChainId,
          })
          .from(accountEvmTxIndexRelationTable)
          .innerJoin(
            accountTable,
            eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            blockNumber: accountPolkadotExtrinsicRelationTable.blockNumber,
            extrinsicIndex:
              accountPolkadotExtrinsicRelationTable.extrinsicIndex,
            chainId: accountTable.polkadotChainId,
          })
          .from(accountPolkadotExtrinsicRelationTable)
          .innerJoin(
            accountTable,
            eq(
              accountPolkadotExtrinsicRelationTable.accountId,
              accountTable.id,
            ),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            signature: accountSolanaSignatureTable.signature,
          })
          .from(accountSolanaSignatureTable)
          .innerJoin(
            accountTable,
            eq(accountSolanaSignatureTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
  ])

  const evmActionIds = await getActionIdsFromExclusiveSourceIds({
    database,
    sourceIds: await getExclusiveEvmSourceIds({
      database,
      selector,
      rows: evmTxRows,
    }),
  })
  const polkadotActionIds = await getActionIdsFromExclusiveSourceIds({
    database,
    sourceIds: await getExclusivePolkadotSourceIds({
      database,
      selector,
      rows: polkadotRows,
    }),
  })
  const solanaActionIds = await getActionIdsFromExclusiveSourceIds({
    database,
    sourceIds: await getExclusiveSolanaSourceIds({
      database,
      selector,
      rows: solanaRows,
    }),
  })
  const actionIds = mergeActionIds({
    actionRows,
    evmActionIds,
    polkadotActionIds,
    solanaActionIds,
  })
  const { deleteActionIds, deletingSources } = await processActions(
    database,
    actionIds,
    selector,
  )

  return {
    actionIds,
    deleteActionIds,
    deletingSources,
    evmTxHashes: evmTxRows.map((row) => row.hash),
    polkadotBlockNumbers: polkadotRows.map((row) => row.blockNumber),
    xrplSourceIds,
    cardanoSourceIds,
    solanaSignatures: solanaRows.map((row) => row.signature),
  }
}

const processActions = async (
  database: DatabaseWithTransaction['database'],
  actionIds: string[],
  selector: AccountSelector,
): Promise<{ deleteActionIds: string[]; deletingSources: string[] }> => {
  if (actionIds.length === 0) {
    return { deleteActionIds: [], deletingSources: [] }
  }

  // Find actions that are linked to other accounts not being deleted
  const actionAccountRelationRowsChunks = await Promise.all(
    divideArrayIntoChunks(actionIds, sqliteMaxHostParameterCount - 1).map(
      (chunk) => {
        const filter = and(
          inArray(actionAccountRelationTable.actionId, [...chunk]),
          createOtherAccountsFilter({
            selector,
            accountIdColumn: actionAccountRelationTable.accountId,
          }),
        )

        return database
          .select({
            actionId: actionAccountRelationTable.actionId,
          })
          .from(actionAccountRelationTable)
          .innerJoin(
            accountTable,
            eq(actionAccountRelationTable.accountId, accountTable.id),
          )
          .where(filter)
      },
    ),
  )

  const actionAccountRelationRows = actionAccountRelationRowsChunks.flat()
  const existOtherAccountActionIdSet = new Set(
    actionAccountRelationRows.map((row) => row.actionId),
  )

  // Determine which actions can be deleted
  const deleteActionIds = actionIds.filter(
    (id) => !existOtherAccountActionIdSet.has(id),
  )

  const deletingSources = await getDeletingSourceIds({
    database,
    deleteActionIds,
  })

  return { deleteActionIds, deletingSources }
}

const createDeletionQueries = (
  context: DeletionContext,
  selector: AccountSelector,
) => {
  const {
    deleteActionIds,
    deletingSources,
    evmTxHashes,
    polkadotBlockNumbers,
    solanaSignatures,
    xrplSourceIds,
    cardanoSourceIds,
  } = context

  const accountFilter = createAccountFilter({ selector })

  return [
    (tx: TransactionDatabase) => [
      deleteAssistantProposalsForActionIds({
        database: tx,
        actionIds: deleteActionIds,
      }),
    ],
    (tx: TransactionDatabase) => [
      // Update portfolio isTxGenerated flag if actions are being deleted
      ...(deleteActionIds.length > 0
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),

      // Update analyzed flags for related transactions
      ...divideArrayIntoChunks(
        xrplSourceIds,
        sqliteMaxHostParameterCount - 1,
      ).flatMap((chunk) => [
        tx
          .update(xrplTransactionIndexTable)
          .set({ analyzed: false })
          .where(inArray(xrplTransactionIndexTable.sourceId, [...chunk])),
        tx
          .update(accountTable)
          .set({ syncing: true })
          .where(
            inArray(
              accountTable.id,
              tx
                .select({ accountId: xrplTransactionIndexTable.accountId })
                .from(xrplTransactionIndexTable)
                .where(inArray(xrplTransactionIndexTable.sourceId, [...chunk])),
            ),
          ),
      ]),
      ...divideArrayIntoChunks(
        cardanoSourceIds,
        sqliteMaxHostParameterCount - 1,
      ).flatMap((chunk) => [
        tx
          .update(cardanoEventIndexTable)
          .set({ analyzed: false })
          .where(inArray(cardanoEventIndexTable.sourceId, [...chunk])),
        tx
          .update(accountTable)
          .set({ syncing: true })
          .where(
            inArray(
              accountTable.id,
              tx
                .select({ accountId: cardanoEventIndexTable.accountId })
                .from(cardanoEventIndexTable)
                .where(inArray(cardanoEventIndexTable.sourceId, [...chunk])),
            ),
          ),
      ]),
      ...divideArrayIntoChunks(evmTxHashes, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .update(accountEvmTxIndexRelationTable)
            .set({ analyzed: false })
            .where(inArray(accountEvmTxIndexRelationTable.hash, [...chunk])),
      ),
      ...divideArrayIntoChunks(
        polkadotBlockNumbers,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .update(accountPolkadotExtrinsicRelationTable)
          .set({ analyzed: false })
          .where(
            inArray(accountPolkadotExtrinsicRelationTable.blockNumber, [
              ...chunk,
            ]),
          ),
      ),
      ...divideArrayIntoChunks(
        solanaSignatures,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .update(accountSolanaSignatureTable)
          .set({ analyzed: false })
          .where(inArray(accountSolanaSignatureTable.signature, [...chunk])),
      ),
    ],
    (tx: TransactionDatabase) => [
      // Delete action sources
      ...divideArrayIntoChunks(
        deletingSources,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(actionSourceTable)
          .where(inArray(actionSourceTable.id, [...chunk])),
      ),
      // Delete accounts
      ...(selector.type === 'group'
        ? [tx.delete(accountTable).where(accountFilter)]
        : divideArrayIntoChunks(
            selector.accountIds,
            sqliteMaxHostParameterCount,
          ).map((chunk) =>
            tx.delete(accountTable).where(inArray(accountTable.id, [...chunk])),
          )),
      // Delete actions
      ...divideArrayIntoChunks(
        deleteActionIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx.delete(actionTable).where(inArray(actionTable.id, [...chunk])),
      ),
    ],
    (tx: TransactionDatabase) => [
      tx
        .delete(accountDisplayOrderTable)
        .where(
          notInArray(
            accountDisplayOrderTable.group,
            tx.select({ group: accountTable.group }).from(accountTable),
          ),
        ),
      // Update remaining actions generatedTx flag
      ...(deleteActionIds.length > 0
        ? [tx.update(actionTable).set({ generatedTx: false })]
        : []),
    ],
  ]
}

/**
 * Creates a factory for account deletion operations.
 * The factory provides methods to delete accounts either by group name
 * or by specific account IDs, handling all related data cleanup.
 *
 * @example
 * ```typescript
 * const factory = createAccountDeletionFactory()
 * await factory.deleteByGroup(database, 'test-group')
 * await factory.deleteByIds(database, [1, 2, 3])
 * ```
 *
 * @returns Factory object with deletion methods
 */
export const createAccountDeletionFactory = () => {
  return {
    async deleteByGroup(database: DatabaseWithTransaction, group: string) {
      const selector: AccountSelector = { type: 'group', group }
      const context = await getRelatedData(database.database, selector)
      const queries = createDeletionQueries(context, selector)
      await database.transaction(queries)
    },

    async deleteByIds(database: DatabaseWithTransaction, accountIds: number[]) {
      if (accountIds.length === 0) {
        return
      }

      const selector: AccountSelector = { type: 'ids', accountIds }
      const context = await getRelatedData(database.database, selector)
      const queries = createDeletionQueries(context, selector)
      await database.transaction(queries)
    },
  }
}
